import { create } from "zustand";
import { api } from "../lib/api";
import type {
  Place,
  OutreachStatus,
  PaginatedResponse,
  LogEngagementInput,
} from "@hsh/shared";

export interface PlaceFilters {
  bbox?: string;
  category?: string;
  location?: string; // Searches address, postcode, and town
  minScore?: number;
  maxScore?: number;
  status?: OutreachStatus;
  noWebsite?: boolean;
  recentlyOpened?: boolean;
  lowReviewVolume?: boolean;
  search?: string;
  // New filters
  noGoogleProfile?: boolean;
  contactedThisWeek?: boolean;
  lowFoodHygiene?: boolean;
  followUpDue?: boolean;
  noPhone?: boolean;
  notEnriched?: boolean;
  noCompaniesHouse?: boolean;
  poorGoogleRating?: boolean;
}

interface MapPlace {
  _id: string;
  name: string;
  address: string;
  category: string;
  score: number;
  lat: number;
  lng: number;
}

interface PlacesState {
  places: Place[];
  mapPlaces: MapPlace[];
  selectedPlace: Place | null;
  heatmapData: Array<{ lat: number; lng: number; score: number }>;
  filters: PlaceFilters;
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
  isLoading: boolean;
  error: string | null;

  setFilters: (filters: Partial<PlaceFilters>) => void;
  resetFilters: () => void;
  fetchPlaces: () => Promise<void>;
  fetchMapPlaces: () => Promise<void>;
  fetchHeatmapData: () => Promise<void>;
  refreshAll: () => Promise<void>;
  selectPlace: (place: Place | null) => void;
  fetchPlace: (id: string) => Promise<void>;
  updateOutreach: (
    placeId: string,
    data: any,
    planId?: string
  ) => Promise<void>;
  logEngagement: (
    placeId: string,
    data: LogEngagementInput,
    planId?: string
  ) => Promise<void>;
  deleteEngagement: (placeId: string, engagementId: string) => Promise<void>;
  updatePlace: (placeId: string, updates: Partial<Place>) => Promise<void>;
  deletePlace: (placeId: string) => Promise<void>;
  setPage: (page: number) => void;
  // URL-based filter methods
  fetchPlacesWithFilters: (
    filters: Partial<PlaceFilters>,
    page?: number
  ) => Promise<void>;
  fetchMapPlacesWithFilters: (filters: Partial<PlaceFilters>) => Promise<void>;
}

const defaultFilters: PlaceFilters = {};

export const usePlacesStore = create<PlacesState>((set, get) => ({
  places: [],
  mapPlaces: [],
  selectedPlace: null,
  heatmapData: [],
  filters: defaultFilters,
  pagination: {
    page: 1,
    pageSize: 50,
    total: 0,
    totalPages: 0,
  },
  isLoading: false,
  error: null,

  setFilters: (newFilters) => {
    set((state) => ({
      filters: { ...state.filters, ...newFilters },
      pagination: { ...state.pagination, page: 1 },
    }));
    get().fetchPlaces();
    get().fetchMapPlaces();
    get().fetchHeatmapData();
  },

  resetFilters: () => {
    set({
      filters: defaultFilters,
      pagination: { ...get().pagination, page: 1 },
    });
    get().fetchPlaces();
    get().fetchMapPlaces();
    get().fetchHeatmapData();
  },

  fetchPlaces: async () => {
    set({ isLoading: true, error: null });
    try {
      const { filters, pagination } = get();
      const response = await api.getPlaces({
        ...filters,
        page: pagination.page,
        pageSize: pagination.pageSize,
      });

      const data = response.data as PaginatedResponse<Place>;
      set({
        places: data.items,
        pagination: {
          page: data.page,
          pageSize: data.pageSize,
          total: data.total,
          totalPages: data.totalPages,
        },
        isLoading: false,
      });
    } catch (error) {
      set({
        error:
          error instanceof Error ? error.message : "Failed to fetch places",
        isLoading: false,
      });
    }
  },

  fetchMapPlaces: async () => {
    try {
      const { filters } = get();
      const response = await api.getMapPlaces({
        category: filters.category,
        location: filters.location,
        minScore: filters.minScore,
        maxScore: filters.maxScore,
        status: filters.status,
        noWebsite: filters.noWebsite,
        recentlyOpened: filters.recentlyOpened,
        lowReviewVolume: filters.lowReviewVolume,
        search: filters.search,
        noGoogleProfile: filters.noGoogleProfile,
        contactedThisWeek: filters.contactedThisWeek,
        lowFoodHygiene: filters.lowFoodHygiene,
        followUpDue: filters.followUpDue,
        noPhone: filters.noPhone,
        notEnriched: filters.notEnriched,
        noCompaniesHouse: filters.noCompaniesHouse,
        poorGoogleRating: filters.poorGoogleRating,
      });
      set({ mapPlaces: response.data });
    } catch (error) {
      console.error("Failed to fetch map places:", error);
    }
  },

  fetchHeatmapData: async () => {
    try {
      const { filters } = get();
      const response = await api.getHeatmapData({
        bbox: filters.bbox,
        category: filters.category,
        location: filters.location,
        minScore: filters.minScore,
        maxScore: filters.maxScore,
        status: filters.status,
        noWebsite: filters.noWebsite,
        recentlyOpened: filters.recentlyOpened,
        lowReviewVolume: filters.lowReviewVolume,
        search: filters.search,
        noGoogleProfile: filters.noGoogleProfile,
        contactedThisWeek: filters.contactedThisWeek,
        lowFoodHygiene: filters.lowFoodHygiene,
        followUpDue: filters.followUpDue,
        noPhone: filters.noPhone,
        notEnriched: filters.notEnriched,
        noCompaniesHouse: filters.noCompaniesHouse,
        poorGoogleRating: filters.poorGoogleRating,
      });
      set({ heatmapData: response.data });
    } catch (error) {
      console.error("Failed to fetch heatmap data:", error);
    }
  },

  selectPlace: (place) => {
    set({ selectedPlace: place });
  },

  fetchPlace: async (id) => {
    try {
      const response = await api.getPlace(id);
      set({ selectedPlace: response.data });
    } catch (error) {
      console.error("Failed to fetch place:", error);
    }
  },

  // Refresh all data (places list, map markers, heatmap) without blocking UI
  refreshAll: async () => {
    const { fetchPlaces, fetchMapPlaces, fetchHeatmapData } = get();
    // Run all fetches in parallel for better performance
    await Promise.all([fetchPlaces(), fetchMapPlaces(), fetchHeatmapData()]);
  },

  updateOutreach: async (placeId, data, planId) => {
    try {
      const response = await api.updateOutreach(placeId, data, planId);
      const updatedPlace = response.data;

      // Update in places list immediately for responsive UI
      set((state) => ({
        places: state.places.map((p) =>
          p._id === placeId ? { ...p, ...updatedPlace } : p
        ),
        selectedPlace:
          state.selectedPlace?._id === placeId
            ? { ...state.selectedPlace, ...updatedPlace }
            : state.selectedPlace,
      }));

      // Refresh map and counts in background
      get().refreshAll();
    } catch (error) {
      throw error;
    }
  },

  logEngagement: async (placeId, data, planId) => {
    try {
      const url = planId
        ? `/api/places/${placeId}/engagement?planId=${planId}`
        : `/api/places/${placeId}/engagement`;

      const response = await api.request<{ success: boolean; data: Place }>(
        url,
        {
          method: "POST",
          body: data,
        }
      );

      if (response.success) {
        const updatedPlace = response.data;
        set((state) => ({
          places: state.places.map((p) =>
            p._id === placeId ? { ...p, ...updatedPlace } : p
          ),
          selectedPlace:
            state.selectedPlace?._id === placeId
              ? updatedPlace
              : state.selectedPlace,
        }));

        // Wait for full refresh of map and counts
        await get().refreshAll();
      }
    } catch (error) {
      throw error;
    }
  },

  deleteEngagement: async (placeId, engagementId) => {
    try {
      const response = await api.request<{ success: boolean; data: Place }>(
        `/api/places/${placeId}/engagement/${engagementId}`,
        {
          method: "DELETE",
        }
      );

      if (response.success) {
        const updatedPlace = response.data;
        set((state) => ({
          places: state.places.map((p) =>
            p._id === placeId ? { ...p, ...updatedPlace } : p
          ),
          selectedPlace:
            state.selectedPlace?._id === placeId
              ? updatedPlace
              : state.selectedPlace,
        }));

        // Wait for full refresh of map and counts
        await get().refreshAll();
      }
    } catch (error) {
      throw error;
    }
  },

  updatePlace: async (placeId, updates) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.request<{ success: boolean; data: Place }>(
        `/api/places/${placeId}`,
        {
          method: "PATCH",
          body: updates,
        }
      );

      if (response.success) {
        const updatedPlace = response.data;
        set((state) => ({
          selectedPlace:
            state.selectedPlace?._id === placeId
              ? updatedPlace
              : state.selectedPlace,
          places: state.places.map((p) =>
            p._id === placeId ? updatedPlace : p
          ),
          mapPlaces: state.mapPlaces.map((p) =>
            p._id === placeId
              ? {
                  ...p,
                  name: updatedPlace.name,
                  address: updatedPlace.address,
                  category: updatedPlace.category,
                  score: updatedPlace.score,
                }
              : p
          ),
        }));

        // Refresh all data in background (for counts, filtered lists, etc.)
        get().refreshAll();
      }
    } catch (error) {
      set({ error: "Failed to update place" });
      console.error(error);
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  deletePlace: async (placeId) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.request<{ success: boolean }>(
        `/api/places/${placeId}`,
        {
          method: "DELETE",
        }
      );

      if (response.success) {
        set((state) => ({
          places: state.places.filter((p) => p._id !== placeId),
          mapPlaces: state.mapPlaces.filter((p) => p._id !== placeId),
          selectedPlace:
            state.selectedPlace?._id === placeId ? null : state.selectedPlace,
        }));

        // Refresh all data in background
        get().refreshAll();
      }
    } catch (error) {
      set({ error: "Failed to delete place" });
      console.error(error);
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  setPage: (page) => {
    set((state) => ({
      pagination: { ...state.pagination, page },
    }));
    get().fetchPlaces();
  },

  // URL-based filter methods - fetch with externally provided filters
  fetchPlacesWithFilters: async (filters, page = 1) => {
    set({ isLoading: true, error: null });
    try {
      const { pagination } = get();
      const response = await api.getPlaces({
        ...filters,
        page: page,
        pageSize: pagination.pageSize,
      });

      const data = response.data as PaginatedResponse<Place>;
      set({
        places: data.items,
        filters: filters as PlaceFilters, // Keep filters in sync
        pagination: {
          page: data.page,
          pageSize: data.pageSize,
          total: data.total,
          totalPages: data.totalPages,
        },
        isLoading: false,
      });
    } catch (error) {
      set({
        error:
          error instanceof Error ? error.message : "Failed to fetch places",
        isLoading: false,
      });
    }
  },

  fetchMapPlacesWithFilters: async (filters) => {
    try {
      const response = await api.getMapPlaces({
        category: filters.category,
        location: filters.location,
        minScore: filters.minScore,
        maxScore: filters.maxScore,
        status: filters.status,
        noWebsite: filters.noWebsite,
        recentlyOpened: filters.recentlyOpened,
        lowReviewVolume: filters.lowReviewVolume,
        search: filters.search,
        noGoogleProfile: filters.noGoogleProfile,
        contactedThisWeek: filters.contactedThisWeek,
        lowFoodHygiene: filters.lowFoodHygiene,
        followUpDue: filters.followUpDue,
        noPhone: filters.noPhone,
        notEnriched: filters.notEnriched,
        noCompaniesHouse: filters.noCompaniesHouse,
        poorGoogleRating: filters.poorGoogleRating,
      });
      set({ mapPlaces: response.data });
    } catch (error) {
      console.error("Failed to fetch map places:", error);
    }
  },
}));
